import { useState, useContext, useMemo, type PropsWithChildren } from 'react'
import { Grid, Select, MenuItem, styledComponent, Hidden, useTheme, Typography, Box } from '@hermes/web-components'
import MobileConditionsProceduresModal from '../../../search/MobileConditionsProceduresModal'
import { AppDataContext } from '../../../../providers/AppData'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { SearchTypes, SortType } from '../../../../providers/SearchProfilesPage/types'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { Insurer, Language } from '../../../../types'
import DefaultSearchBar from '../../Default/SearchBar'
import AlternativeSearchBar from '../../Alternative/SearchBar'
import DefaultSearchFilter from '../../Default/SearchBar/SearchFilters'
import AlternativeSearchFilter from '../../Alternative/SearchBar/SearchFilters'
import ContentTopContainer from '../../../ContentTopContainer'
import { TEST_IDS, SORT_BY_OPTIONS } from '../../../../__tests__/testUtils/constants'
import useLayoutConfig from '../../../search/hooks/useLayoutConfig'
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags'
import { TENANTS } from '../../../../constants/global'

type SearchLayoutProps = {
  languages: Language[]
  insurers: Insurer[]
} & PropsWithChildren<object>

const MediumBoldTypography = styledComponent('h1')(({ theme }) => ({
  fontSize: '15px',
  lineHeight: '20px',
  fontWeight: 500,
  margin: 0,
  color: theme.palette.text.black900,

  '&::first-letter': {
    textTransform: 'uppercase'
  },
  [theme.breakpoints.down('md')]: {
    paddingLeft: `${theme.spacing(2)}`,
    paddingRight: `${theme.spacing(2)}`
  }
}))

const SearchResultsLayout = styledComponent(Grid)(({ theme }) => ({
  minHeight: '100vh',
  gap: '16px',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    paddingInline: '16px'
  }
}))

const searchTotalTranslateKey = 'search.new_total_found'

// TODO: fix complexity
// eslint-disable-next-line complexity
function SearchLayout({ children, languages, insurers }: SearchLayoutProps) {
  const translate = useTranslateMessage()
  const theme = useTheme()

  const { alternativeSearchEnabled } = useFeatureFlags()

  const { language, locale } = useContext(AppDataContext)
  const {
    changeFilter,
    searchKeywordTitle,
    state: { filters, typeOfSpecialist, specialty, totalProfiles }
  } = useContext(SearchProfilesPageContext)

  const { displayComponents } = useLayoutConfig()

  const [isOpenConditionsProceduresModal, setIsOpenConditionsProceduresModal] = useState(false)

  const sorting = useMemo(() => {
    const defaultSorting = [
      {
        value: SortType.relevance,
        label: translate('relevance'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.RELEVANCE}`
      },
      {
        value: SortType.nearest,
        label: translate('nearest'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.NEAREST}`
      },
      {
        value: SortType.rating,
        label: translate('rating'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.RATING}`
      }
    ]
    if (filters.searchType === SearchTypes.specialists) {
      defaultSorting.push({
        value: SortType.recommended,
        label: translate('most_endorsements'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.MOST_ENDORSEMENTS}`
      })
    }

    return defaultSorting
  }, [filters.searchType, translate])

  const renderSearchFilters = (postAction?: () => void) =>
    alternativeSearchEnabled && locale !== TENANTS.WPA ? (
      <AlternativeSearchFilter languages={languages} insurers={insurers} specialties={typeOfSpecialist} />
    ) : (
      <DefaultSearchFilter
        postAction={postAction}
        languages={languages}
        insurers={insurers}
        specialties={typeOfSpecialist}
        specialtyName={specialty?.name[language] || ''}
      />
    )

  const location = filters.location || locale
  const parentLocation = filters.parentLocation ? `, ${filters.parentLocation}` : ''
  const decodedLocation = decodeURI(location + parentLocation)

  const translatedName = useMemo(() => {
    switch (filters.searchType) {
      case SearchTypes.healthAndSocialCare:
        return translate('home-care-specs')
      case SearchTypes.hospital:
        return translate('hospitals')
      case SearchTypes.pharmacy:
        return translate('pharmacies')
      case SearchTypes.specialists:
        return translate('specialists')
      case SearchTypes.careHome:
        return translate('care-home')
      default:
        return translate('practices')
    }
  }, [filters.searchType, translate])

  const getTranslatedTotalFound = () => {
    const locationText = decodeURIComponent(decodedLocation)

    switch (filters.searchType) {
      case SearchTypes.specialists:
        return translate(searchTotalTranslateKey, {
          location: locationText,
          name: specialty.id === 1 ? translate('search.all_specialists') : `${searchKeywordTitle || ''}`
        })

      case SearchTypes.dentist:
      case SearchTypes.practices:
        return translate('search.new_total_found-practice', {
          location: locationText,
          name: specialty?.name?.[language]
        })

      case SearchTypes.pharmacy:
      case SearchTypes.hospital:
      case SearchTypes.careHome:
      case SearchTypes.healthAndSocialCare:
        return translate(searchTotalTranslateKey, { location: locationText, name: translatedName })

      default:
        return translate(searchTotalTranslateKey, { location: locationText, name: specialty?.name?.[language] })
    }
  }

  return (
    <Grid sx={{ pb: 3, overflowX: 'hidden', ...theme.custom.SearchLayout }} container direction="column">
      {alternativeSearchEnabled && locale !== TENANTS.WPA ? (
        <AlternativeSearchBar renderSearchFilters={renderSearchFilters} sorting={sorting} />
      ) : (
        <DefaultSearchBar renderSearchFilters={renderSearchFilters} sorting={sorting} />
      )}
      <ContentTopContainer
        withKeywords={displayComponents.relatedKeywords}
        isWPA={locale === TENANTS.WPA}
        isSearchPage
        maxWidth="xl"
      >
        <Grid container direction="row" wrap="nowrap" gap="24px">
          <Grid component={Hidden} lgDown implementation="css" xs={2}>
            <Hidden lgDown>{renderSearchFilters()}</Hidden>
          </Grid>
          <SearchResultsLayout alignContent="flex-start" container>
            <Grid
              container
              gap="15px"
              justifyContent="space-between"
              alignItems="center"
              data-testid={TEST_IDS.SEARCH_RESULTS_TITLE}
              xs={12}
            >
              <Box display="flex" alignItems="center" gap="4px">
                <Hidden implementation="css" smUp>
                  <MediumBoldTypography color="text.secondary">
                    {`${getTranslatedTotalFound()} ${translate('search.results', { total: totalProfiles })}`}
                  </MediumBoldTypography>
                </Hidden>

                <Hidden smDown>
                  <MediumBoldTypography color="text.secondary">{getTranslatedTotalFound()}</MediumBoldTypography>
                  <Typography fontSize="15px" lineHeight="20px" fontWeight={500}>
                    {translate('search.results', { total: totalProfiles })}
                  </Typography>
                </Hidden>
              </Box>
              <Hidden mdDown>
                <Select
                  size="small"
                  value={filters.sortType}
                  displayEmpty
                  onChange={(ev) => changeFilter('sortType', ev.target.value as string)}
                >
                  {sorting.map((sort) => (
                    <MenuItem key={sort.value} value={sort.value}>
                      {sort.label}
                    </MenuItem>
                  ))}
                </Select>
              </Hidden>
            </Grid>
            {children}
          </SearchResultsLayout>
        </Grid>
      </ContentTopContainer>
      <MobileConditionsProceduresModal
        isOpen={isOpenConditionsProceduresModal}
        onClose={() => setIsOpenConditionsProceduresModal(false)}
        specialtyName={specialty?.name[language] || ''}
      />
    </Grid>
  )
}

export default SearchLayout
